import {Component, Input, OnInit} from '@angular/core';
import {BreakpointObserver} from '@angular/cdk/layout';
import {Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {CommerceConfig} from '@app/config/CommerceConfig';
import {
  DcpMedia,
  DcpMediaQuality,
  IMAGE_FORMAT,
  IMAGE_FORMAT_LOWERCASE,
  VIDEO_FORMAT_LOWERCASE,
  VIMEO_VIDEO_FORMAT,
  YOUTUBE_VIDEO_FORMAT,
} from '@app/model/dcp-media';
import {BreakpointsConfig} from '@app/model/breakpoint.model';
import {ScreenService} from '@app/services/screen/screen.service';
import {YouTubePlayer} from '@angular/youtube-player';
import {MediaComponent} from '@spartacus/storefront';
import {AsyncPipe} from '@angular/common';
import {VimeoUrlPipe} from '../../pipes/vimeo-url/vimeo-url.pipe';
import {YouTubeUrlPipe} from '../../pipes/you-tube-url/you-tube-url.pipe';

const DEF_RATIO = [16, 9];
const DEF_WIDTH = 560;

@Component({
  selector: 'dcp-media',
  templateUrl: './dcp-media.component.html',
  styleUrls: ['./dcp-media.component.scss'],
  imports: [YouTubePlayer, MediaComponent, AsyncPipe, VimeoUrlPipe, YouTubeUrlPipe],
})
export class DcpMediaComponent implements OnInit {
  @Input() media: DcpMedia;

  mediaQuality: DcpMediaQuality;
  sW$: Observable<number>;
  sH$: Observable<number>;

  private readonly youtubePatterns = [
    /youtu\.be\/([\w-]{11})/, // youtu.be/VIDEOID
    /youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=)([\w-]{11})/, // youtube.com/embed/VIDEOID etc.
    /youtube-nocookie\.com\/embed\/([\w-]{11})/, // youtube-nocookie.com/embed/VIDEOID
  ];

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly screenService: ScreenService,
  ) {
    this.sW$ = this.breakpointObserver
      .observe(`(max-width: ${(CommerceConfig as BreakpointsConfig).breakpoints.sm}px)`)
      .pipe(
        switchMap((breakpoint) => {
          if (breakpoint.matches) {
            return this.screenService.getCurrentScreenWidth$({targetCheck: 'dcp-media', elemCheck: '.udex-grid'});
          }
          return of(DEF_WIDTH);
        }),
      );
    this.sH$ = this.sW$.pipe(map((width) => Math.round((width * DEF_RATIO[1]) / DEF_RATIO[0])));
  }

  ngOnInit(): void {
    this.mediaQuality = this.media?.quality || 'low';
  }

  isVisible(): boolean {
    return (
      this.isVimeoMedia() ||
      this.isYouTubeMedia() ||
      this.isImageMedia() ||
      this.isUnspecifiedYoutubeVideoMedia() ||
      this.isUnspecifiedVimeoVideoMedia()
    );
  }

  isVimeoMedia(): boolean {
    return this.media && this.media.format === VIMEO_VIDEO_FORMAT;
  }

  isYouTubeMedia(): boolean {
    return this.media && this.media.format === YOUTUBE_VIDEO_FORMAT;
  }

  isImageMedia(): boolean {
    return this.media && (this.media.format === IMAGE_FORMAT || this.media.format === IMAGE_FORMAT_LOWERCASE);
  }

  isUnspecifiedYoutubeVideoMedia(): boolean {
    return this.media && this.media.format === VIDEO_FORMAT_LOWERCASE && this.verifyYoutubeUrl(this.media.url);
  }

  isUnspecifiedVimeoVideoMedia(): boolean {
    return (
      this.media &&
      this.media.format === VIDEO_FORMAT_LOWERCASE &&
      (this.media.url?.includes('https://www.vimeo.com') ||
        this.media.url?.includes('https://vimeo.com') ||
        this.media.url?.includes('https://player.vimeo.com'))
    );
  }

  verifyYoutubeUrl(url: string): boolean {
    if (!url) return false;

    for (const pattern of this.youtubePatterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return true;
      }
    }
    return false;
  }
}
